import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';

import { TranslocoModule } from '@jsverse/transloco';
import { fromEvent, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { ENVIRONMENT } from '../../environments/environment.token';
import { AuthService } from '../auth.service';
import { MicrosoftAuthService } from '../microsoft-auth.service';


declare global {
  interface Window {
    google: any;
  }
}

@Component({
  selector: 'shared-core-external-login',
  standalone: true,
  imports: [ CommonModule, TranslocoModule ],
  templateUrl: './external-login.component.html',
  styleUrls: [ './external-login.component.scss' ],
})
export class ExternalLoginComponent implements AfterViewInit, OnDestroy {
  @ViewChild('googleButtonDiv') googleButtonDiv!: ElementRef;

  loginFailed = output<void>();

  microsoftPending = signal(false);

  private subscription = new Subscription();
  private environment = inject(ENVIRONMENT);
  private authService = inject(AuthService);
  private microsoftAuthService = inject(MicrosoftAuthService);

  // Hidden rather than disabled when unconfigured: an environment without the Entra app
  // registration should show no button at all, not one that always fails. This is not
  // per-customer gating — the login page is the same for every customer.
  get showMicrosoft(): boolean {
    return this.microsoftAuthService.isConfigured;
  }

  // AfterViewInit, not OnInit: rendering the button needs the googleButtonDiv
  // view child, which does not exist yet when the readyState branch below
  // runs synchronously.
  ngAfterViewInit() {
    this.loadGoogleSigningScript();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async signInWithMicrosoft() {
    if (this.microsoftPending()) {
      return;
    }

    this.microsoftPending.set(true);
    try {
      const token = await this.microsoftAuthService.signIn();

      // Null means the user closed the popup. Nothing failed, so say nothing.
      if (token === null) {
        this.microsoftPending.set(false);

        return;
      }

      // Cleared from the callbacks, not from a finally: subscribe() returns as soon as the
      // request is dispatched, so releasing the flag here would re-enable the button while
      // the login is still in flight and let a second click open a second popup.
      this.subscription.add(
        this.authService.microsoftLogin(token).subscribe({
          next: () => this.microsoftPending.set(false),
          error: () => {
            this.microsoftPending.set(false);
            this.loginFailed.emit();
          },
        })
      );
    } catch {
      this.microsoftPending.set(false);
      this.loginFailed.emit();
    }
  }

  // Delay Google Sign-In til the entire window (and async scripts) has loaded.
  // When the component is created after the page load (e.g. router navigation
  // back to the login page), the load event has already fired and never will
  // again, so initialize immediately.
  private loadGoogleSigningScript() {
    if (document.readyState === 'complete') {
      this.initGoogleSignIn();
    } else {
      fromEvent(window, 'load')
        .pipe(take(1))
        .subscribe(() => this.initGoogleSignIn());
    }
  }

  private initGoogleSignIn() {
    if (!window.google) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: this.environment.googleSignInClientId,
      context: 'signin',
      ux_mode: 'popup',
      auto_prompt: false,
      callback: this.handleGoogleResponse.bind(this),
    });

    this.renderGoogleSignIn();
  }

  private renderGoogleSignIn() {
    const googleButtonDiv = this.googleButtonDiv.nativeElement;

    if (googleButtonDiv) {
      window.google.accounts.id.renderButton(googleButtonDiv, {
        theme: 'outline',
        type: 'standard',
        shape: 'pill',
        width: 344,
        text: 'signin_with',
        size: 'large',
        logo_alignment: 'center',
      });
    }
  }

  // The Google Identity Services callback runs outside the Angular zone. No
  // zone wrapping is needed: downstream UI updates are signal-based, which the
  // change detection scheduler picks up regardless of zone (and in zoneless mode).
  private handleGoogleResponse(response: any) {
    this.subscription.add(
      this.authService.googleLogin(response.credential).subscribe({
        error: () => this.loginFailed.emit(),
      })
    );
  }
}
